<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Rapport de Stock</h1>
        <p class="page-subtitle" v-if="loading">Chargement… {{ rows.length }}{{ total !== null ? ' / ' + total : '' }} produits chargés</p>
        <p class="page-subtitle" v-else-if="rows.length">{{ displayRows.length }}{{ total !== null ? ' / ' + total : '' }} produit{{ displayRows.length > 1 ? 's' : '' }} — Valeur: {{ fmt(totalCostFinal) }}</p>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <v-btn color="error" outlined small @click="exportPDF" v-if="rows.length"><v-icon left small>mdi-file-pdf-box</v-icon>PDF</v-btn>
        <v-btn color="success" outlined small @click="exportExcel" v-if="rows.length"><v-icon left small>mdi-file-excel</v-icon>Excel</v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <v-alert v-if="loadError" type="warning" text dense class="mb-3">
        Chargement interrompu — les données affichées sont incomplètes.
        <a @click.prevent="loadAll()" style="text-decoration: underline; cursor: pointer;">Réessayer</a>
      </v-alert>
      <v-alert v-else-if="!loading && total !== null && rows.length !== total" type="warning" text dense class="mb-3">
        Écart détecté : {{ rows.length }} produit(s) affiché(s) sur {{ total }} attendus.
        <a @click.prevent="loadAll()" style="text-decoration: underline; cursor: pointer;">Recharger</a>
      </v-alert>
      <!-- Summary cards -->
      <v-row class="mb-2">
        <v-col cols="6" md>
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#194173"><v-icon color="white" size="18">mdi-package-variant</v-icon></div>
            <div class="summary-val">{{ fmt(totalStock) }}</div>
            <div class="summary-lbl">Stock Total</div>
          </v-card>
        </v-col>
        <v-col cols="6" md>
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#f68d3b"><v-icon color="white" size="18">mdi-cash</v-icon></div>
            <div class="summary-val">{{ fmt(totalCostFinal) }}</div>
            <div class="summary-lbl">Valeur au Coût</div>
          </v-card>
        </v-col>
        <v-col cols="6" md>
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#4CAF50"><v-icon color="white" size="18">mdi-chart-line</v-icon></div>
            <div class="summary-val">{{ fmt(totalCaPrev) }}</div>
            <div class="summary-lbl">CA Prévisionnel</div>
          </v-card>
        </v-col>
        <v-col cols="6" md>
          <v-card flat class="summary-card summary-card--clickable" :class="{ 'summary-card--active': onlyNegative }" @click="toggleNegative">
            <div class="summary-icon-sm" style="background:#F44336"><v-icon color="white" size="18">mdi-alert</v-icon></div>
            <div class="summary-val error--text">{{ negativeCount }}</div>
            <div class="summary-lbl">Stock Négatif</div>
          </v-card>
        </v-col>
        <v-col cols="6" md>
          <v-card flat class="summary-card summary-card--clickable" :class="{ 'summary-card--active': onlySales }" @click="onlySales = !onlySales">
            <div class="summary-icon-sm" style="background:#0e7490"><v-icon color="white" size="18">mdi-cart-outline</v-icon></div>
            <div class="summary-val">{{ salesCount }}</div>
            <div class="summary-lbl">Avec Ventes</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-card flat class="filter-card mb-3">
        <v-card-text class="py-3">
          <v-row dense align="center">
            <v-col cols="12" sm="4" md="2">
              <v-select
                v-model="store"
                :items="stores"
                item-text="name"
                item-value="id"
                outlined dense hide-details
                placeholder="Emplacement"
                prepend-inner-icon="mdi-store-outline"
                @change="onStoreChange"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4" md="2">
              <v-select
                v-model="category"
                :items="categories"
                item-text="name"
                item-value="id"
                outlined dense hide-details clearable
                placeholder="Toutes les catégories"
                prepend-inner-icon="mdi-folder-outline"
                @change="onCategoryChange"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="5" md="3">
              <v-text-field
                v-model="search"
                outlined dense hide-details clearable
                placeholder="Rechercher un produit..."
                prepend-inner-icon="mdi-magnify"
                @input="onSearchInput"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="auto">
              <div class="filter-chips">
                <button type="button" class="filter-chip filter-chip--neg" :class="{ 'filter-chip--on': onlyNegative }" @click="toggleNegative">
                  <span class="chip-dot chip-dot--neg"></span>
                  Stock négatif
                  <span class="chip-count">{{ negativeCount }}</span>
                </button>
                <button type="button" class="filter-chip filter-chip--low" :class="{ 'filter-chip--on': onlyLow }" @click="toggleLow">
                  <span class="chip-dot chip-dot--low"></span>
                  Stock faible
                  <span class="chip-count">{{ lowCount }}</span>
                </button>
                <button type="button" class="filter-chip filter-chip--sales" :class="{ 'filter-chip--on': onlySales }" @click="onlySales = !onlySales">
                  <span class="chip-dot chip-dot--sales"></span>
                  Avec ventes
                  <span class="chip-count">{{ salesCount }}</span>
                </button>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">PRODUIT</th>
                <th class="text-left">CATÉGORIE</th>
                <th class="text-right">INITIAL</th>
                <th class="text-right">VENTES</th>
                <th class="text-right">RÉCEPTIONS</th>
                <th class="text-right">AJUST.</th>
                <th class="text-right">STOCK FINAL</th>
                <th class="text-right">VALEUR COÛT</th>
                <th class="text-right">CA PRÉV.</th>
              </tr>
              <tr class="totals-row">
                <th class="text-left" colspan="2">TOTAL ({{ displayRows.length }})</th>
                <th class="text-right">{{ fmt(sumField('initial')) }}</th>
                <th class="text-right">{{ fmt(sumField('sale')) }}</th>
                <th class="text-right">{{ fmt(sumField('reception')) }}</th>
                <th class="text-right">{{ fmt(sumAdj) }}</th>
                <th class="text-right">{{ fmt(totalStock) }}</th>
                <th class="text-right">{{ fmt(totalCostFinal) }}</th>
                <th class="text-right">{{ fmt(totalCaPrev) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in displayRows" :key="idx">
                <td class="text-left font-weight-medium">{{ item.product_name }}</td>
                <td class="text-left text-caption text--secondary">{{ item.category }}</td>
                <td class="text-right">{{ item.initial }}</td>
                <td class="text-right">{{ item.sale }}</td>
                <td class="text-right">{{ item.reception }}</td>
                <td class="text-right">{{ item.ajustement + item.other }}</td>
                <td class="text-right font-weight-bold" :class="getFinal(item) < 0 ? 'error--text' : ''">{{ getFinal(item) }}</td>
                <td class="text-right">{{ fmt(getCost(item)) }}</td>
                <td class="text-right">{{ fmt(getCa(item)) }}</td>
              </tr>
              <tr v-if="displayRows.length === 0 && !loading">
                <td colspan="9" class="text-center text--secondary pa-6">Aucune donnée</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

var PAGE_SIZE = 200
var LOW_STOCK_MAX = 10

export default {
  name: 'StockView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Rapport de Stock', disabled: true, href: '/stock' }],
    enable: false,
    search: '',
    rows: [],
    loading: false,
    loadError: false,
    generation: 0,
    total: null,
    categories: [],
    category: null,
    stores: [],
    store: null,
    searchTimer: null,
    onlyNegative: false,
    onlyLow: false,
    onlySales: false,
  }),

  computed: {
    displayRows() {
      var self = this
      var out = this.rows
      if (this.onlyNegative) out = out.filter(function (r) { return self.getFinal(r) < 0 })
      if (this.onlyLow) out = out.filter(function (r) { return self.isLowStock(r) })
      if (this.onlySales) out = out.filter(function (r) { return (r.sale || 0) > 0 })
      return out
    },
    salesCount() { return this.rows.filter(function (r) { return (r.sale || 0) > 0 }).length },
    lowCount() { var self = this; return this.rows.filter(function (r) { return self.isLowStock(r) }).length },
    totalStock() { var self = this; return this.displayRows.reduce(function (s, r) { return s + self.getFinal(r) }, 0) },
    totalCostFinal() { var self = this; return this.displayRows.reduce(function (s, r) { return s + self.getCost(r) }, 0) },
    totalCaPrev() { var self = this; return this.displayRows.reduce(function (s, r) { return s + self.getCa(r) }, 0) },
    sumAdj() { return this.displayRows.reduce(function (s, r) { return s + (r.ajustement || 0) + (r.other || 0) }, 0) },
    negativeCount() { var self = this; return this.rows.filter(function (r) { return self.getFinal(r) < 0 }).length },
  },

  methods: {
    toggleNegative() { this.onlyNegative = !this.onlyNegative; if (this.onlyNegative) this.onlyLow = false },
    toggleLow() { this.onlyLow = !this.onlyLow; if (this.onlyLow) this.onlyNegative = false },
    getFinal(item) { return item.initial - item.sale + item.reception + item.other + item.ajustement },
    // Seuil « stock faible » : le seuil propre au produit (stock_alert) s'il est défini
    // (> 0), sinon le plancher historique LOW_STOCK_MAX. Aligné sur le widget d'accueil,
    // qui applique le même seuil par produit.
    lowThreshold(item) {
      var alert = item.product && item.product.stock_alert != null ? parseFloat(item.product.stock_alert) : 0
      return alert > 0 ? alert : LOW_STOCK_MAX
    },
    isLowStock(item) { var f = this.getFinal(item); return f >= 0 && f <= this.lowThreshold(item) },
    getCost(item) { var cost = item.product && item.product.cost != null ? parseFloat(item.product.cost) : 0; return cost * this.getFinal(item) },
    getCa(item) { var price = item.product && item.product.price != null ? parseFloat(item.product.price) : 0; return price * this.getFinal(item) },
    sumField(field) { return this.displayRows.reduce(function (s, r) { return s + (r[field] || 0) }, 0) },

    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return (parseFloat(v) < 0 ? '-' : '') + p.join('.')
    },

    buildQuery(offset) {
      var isValid = function (v) { return v && v !== 'null' && v.trim() !== '' }
      var from = isValid(localStorage.debut_periode) ? localStorage.debut_periode : ''
      var to = isValid(localStorage.fin_periode) ? localStorage.fin_periode : ''
      var url = '/reports/stock?include=product&from=' + from + '&to=' + to +
        '&offset=' + offset + '&limit=' + PAGE_SIZE
      if (this.category) url += '&category=' + this.category
      if (this.search && this.search.trim()) url += '&name=' + encodeURIComponent(this.search.trim())
      if (this.store) url += '&store_id=' + this.store
      return url
    },

    async loadAll() {
      var gen = ++this.generation
      this.rows = []
      this.total = null
      this.loading = true
      this.loadError = false
      var offset = 0
      try {
        for (;;) {
          var r = await this.$axios.get(this.buildQuery(offset))
          if (gen !== this.generation) return
          if (r.data && r.data.success === false) { this.loadError = true; break }
          if (r.data && typeof r.data.total === 'number') this.total = r.data.total
          var page = (r.data && r.data.result) || []
          this.rows = this.rows.concat(page)
          this.enable = true
          if (this.total !== null && this.rows.length >= this.total) break
          if (page.length < PAGE_SIZE) break
          offset += PAGE_SIZE
        }
      } catch (e) {
        if (gen !== this.generation) return
        this.loadError = true
      }
      this.enable = true
      this.loading = false
    },

    loadCategories() {
      var self = this
      this.$axios.get('/categories?orderasc=name')
        .then(function (r) { self.categories = r.data || [] })
        .catch(function () {})
    },

    loadStores() {
      var self = this

      // Parse défensif (même motif que le reste de l'app, cf. AppLayout / IngredientStockMovementView) :
      // un `JSON.parse(localStorage.business)` brut lève de façon SYNCHRONE dans mounted() dès que la
      // clé est absente ou vaut la chaîne "undefined". La chaîne .catch().then(loadAll) n'était alors
      // jamais construite, donc loadAll() n'était jamais appelé et le tableau ne montait pas.
      var biz = {}
      try { biz = JSON.parse(localStorage.business || '{}') || {} } catch (e) { biz = {} }
      var businessId = biz.id

      if (!businessId) {
        // Pas de business exploitable : on charge quand même le rapport (le filtre magasin
        // reste simplement vide) plutôt que de laisser la vue bloquée.
        this.loadAll()
        return Promise.resolve()
      }

      return this.$axios.get('/stores?orderasc=name&business_id=' + businessId)
        .then(function (r) {
          self.stores = r.data || []
          if (self.stores.length && !self.store) self.store = self.stores[0].id
        })
        .catch(function () {})
        .then(function () { self.loadAll() })
    },

    onCategoryChange() {
      this.loadAll()
    },

    onStoreChange() {
      this.loadAll()
    },

    onSearchInput() {
      var self = this
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(function () { self.loadAll() }, 400)
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], border = [220, 225, 230], light = [248, 250, 252]

      doc.setFillColor(...primary); doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('RAPPORT DE STOCK', ml, 11)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      doc.text((localStorage.debut_periode || '') + ' — ' + (localStorage.fin_periode || ''), mr, 8, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 13, { align: 'right' })

      var y = 24
      var cols = [
        { label: 'PRODUIT', w: 42, align: 'left' },
        { label: 'CATÉGORIE', w: 28, align: 'left' },
        { label: 'INITIAL', w: 16, align: 'right' },
        { label: 'VENTES', w: 16, align: 'right' },
        { label: 'RÉCEPT.', w: 16, align: 'right' },
        { label: 'AJUST.', w: 16, align: 'right' },
        { label: 'FINAL', w: 16, align: 'right' },
        { label: 'VAL. COÛT', w: 22, align: 'right' },
        { label: 'CA PRÉV.', w: 22, align: 'right' },
      ]
      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW; cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0); cols[0].w += (cw - actualW)
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 6.5

      function drawHeader() {
        doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : c.x + 3; doc.text(c.label, tx, y + 4.5, { align: c.align }) })
        y += rowH + 1
      }
      drawHeader()

      self.displayRows.forEach(function (r, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var fin = self.getFinal(r)
        var name = r.product_name || ''; if (name.length > 22) name = name.substring(0, 20) + '..'
        var cat = r.category || ''; if (cat.length > 14) cat = cat.substring(0, 12) + '..'
        var vals = [name, cat, String(r.initial), String(r.sale), String(r.reception), String(r.ajustement + r.other), String(fin), self.fmt(self.getCost(r)), self.fmt(self.getCa(r))]
        cols.forEach(function (c, i) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : c.x + 3
          if (i === 6 && fin < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(...dark)
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      // Totals
      y += 1; doc.setFillColor(...light); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(...primary); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...dark)
      doc.text('TOTAL', ml + 3, y + 4.8)
      doc.text(String(Math.round(self.totalStock)), cols[6].x + cols[6].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.totalCostFinal), cols[7].x + cols[7].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.totalCaPrev), cols[8].x + cols[8].w - 3, y + 4.8, { align: 'right' })

      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p); doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('rapport_stock.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var wsData = [['RAPPORT DE STOCK'], [biz.name || ''], ['Du ' + (localStorage.debut_periode || '') + ' au ' + (localStorage.fin_periode || '')], []]
      wsData.push(['Produit', 'Catégorie', 'Initial', 'Ventes', 'Réceptions', 'Ajustements', 'Stock Final', 'Valeur Coût', 'CA Prévisionnel'])
      self.displayRows.forEach(function (r) {
        wsData.push([r.product_name, r.category, r.initial, r.sale, r.reception, r.ajustement + r.other, self.getFinal(r), Math.round(self.getCost(r) * 100) / 100, Math.round(self.getCa(r) * 100) / 100])
      })
      wsData.push([]); wsData.push(['TOTAL', '', self.sumField('initial'), self.sumField('sale'), self.sumField('reception'), self.sumAdj, Math.round(self.totalStock * 100) / 100, Math.round(self.totalCostFinal * 100) / 100, Math.round(self.totalCaPrev * 100) / 100])
      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: 4, c: 8 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } }]
      for (var r = 5; r < wsData.length; r++) { for (var c = 7; c <= 8; c++) { var cell = ws[XLSX.utils.encode_cell({ r: r, c: c })]; if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = '#,##0.00' } } }
      XLSX.utils.book_append_sheet(wb, ws, 'Stock'); XLSX.writeFile(wb, 'rapport_stock.xlsx')
    },
  },

  mounted() { this.loadCategories(); this.loadStores() },

  beforeDestroy() { clearTimeout(this.searchTimer); this.generation++ },
}
</script>

<style scoped>
.summary-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; padding: 16px; text-align: center; }
.summary-icon-sm { width: 36px; height: 36px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.summary-val { font-size: 1.125rem; font-weight: 700; color: #1a2332; }
.summary-lbl { font-size: 0.75rem; color: #64748b; margin-top: 2px; }
.filter-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.filter-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.filter-chip { display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 6px 12px; border-radius: 999px; border: 1.5px solid #cbd5e1; background: #fff; color: #475569; font-size: 0.78rem; font-weight: 600; cursor: pointer; white-space: nowrap; transition: border-color .15s ease, background .15s ease, color .15s ease, transform .1s ease; outline: none; }
.filter-chip:hover { border-color: #194173; color: #194173; }
.filter-chip:active { transform: scale(0.97); }
.filter-chip:focus-visible { box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.25); }
.chip-dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; }
.chip-dot--neg { background: #dc2626; }
.chip-dot--low { background: #f59e0b; }
.chip-dot--sales { background: #0e7490; }
.chip-count { font-size: 0.6875rem; font-weight: 700; padding: 1px 7px; border-radius: 999px; background: #f1f5f9; color: #64748b; }
.filter-chip--neg.filter-chip--on { background: #fef2f2; border-color: #dc2626; color: #b91c1c; }
.filter-chip--neg.filter-chip--on .chip-count { background: #dc2626; color: #fff; }
.filter-chip--low.filter-chip--on { background: #fffbeb; border-color: #d97706; color: #b45309; }
.filter-chip--low.filter-chip--on .chip-count { background: #d97706; color: #fff; }
.filter-chip--sales.filter-chip--on { background: #f0fdfa; border-color: #0e7490; color: #0e7490; }
.filter-chip--sales.filter-chip--on .chip-count { background: #0e7490; color: #fff; }
@media (max-width: 599px) {
  .filter-chips { width: 100%; }
  .filter-chip { flex: 1 1 40%; justify-content: center; min-height: 40px; }
}
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-responsive { overflow-x: auto; max-height: 600px; overflow-y: auto; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th { position: sticky; top: 0; background: #f8fafc; font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 12px; border-bottom: 2px solid #e8ecf1; white-space: nowrap; z-index: 1; }
.report-table .totals-row th { background: #f1f5f9; color: #1a2332; font-weight: 700; font-size: 0.75rem; border-bottom: 2px solid #cbd5e1; }
.report-table tbody td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }
.summary-card--clickable { cursor: pointer; transition: box-shadow .15s, border-color .15s; }
.summary-card--clickable:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; }
.summary-card--active { border-color: #194173 !important; box-shadow: 0 0 0 2px rgba(25,65,115,0.25) !important; }
</style>
